import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// --------------------- Import Antd-Design ---------------------
import {
  Modal,
  Skeleton,
  Form,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Select,
  Card,
} from "antd";

// --------------------- Import Styles ---------------------
import { layout, Title } from "../styles";

// --------------------- Import Hooks ---------------------
import { useAtualizarEmpreendimento } from "../hooks/useAtualizarEmpreendimento";
import { useEmpreendimentoById } from "../hooks/useEmpreendimentoUsuario";
import { useCidade } from "../hooks/useCidade";
import { useCidadeEmpreend } from "../hooks/useCidadeEmpreend";

// --------------------- Import Interfaces ---------------------
import { Cidade } from "../../../interfaces/Cidade";
import { ModalEmpreendProps } from "../interfaces/ModalEmpreendProps";
import { Empreendimento } from "../../../interfaces/Empreendimento";

// --------------------- Import Uteis ---------------------
import { useMessage } from "../../../uteis/mensagens/useMessage";
import { filterOption, filterSort } from "../../../uteis/filtroSelect";

export default function ModalEditar({
  isModalOpen,
  handleFechar,
  empreendId,
}: ModalEmpreendProps) {
  const [formEditEmp] = Form.useForm();
  const changing = useRef<boolean>(false);
  const navigate = useNavigate();

  const { empreendimentoById, isLoading } = useEmpreendimentoById(
    empreendId,
    isModalOpen
  );
  const { cidade } = useCidadeEmpreend(
    isModalOpen,
    empreendimentoById?.cidadeId
  );
  const { cidades } = useCidade(isModalOpen, cidade?.estadoId);
  const {
    atualizarEmpreendimento,
    error,
    success,
    resetError,
    isLoading: isLoadingAtualizar,
  } = useAtualizarEmpreendimento();
  const { showSuccess, showError, contextHolder } = useMessage();

  const [empreendimento, setEmpreendimento] = useState<Partial<Empreendimento>>(
    {}
  );
  const [cronograma, setCronograma] = useState<number>();

  const cronogramaChange = (e: RadioChangeEvent) => {
    setCronograma(e.target.value);
    formEditEmp.setFieldsValue({ cronograma: e.target.value });
  };

  // Atualiza os campos do form quando os dados do backend chegam
  useEffect(() => {
    if (empreendimentoById && isModalOpen) {
      setEmpreendimento({
        nome: empreendimentoById.nome,
        cidadeId: empreendimentoById.cidadeId,
        origem: empreendimentoById.origem,
        cronograma: empreendimentoById.cronograma,
        endereco: empreendimentoById.endereco,
        contato: empreendimentoById.contato,
        descricao: empreendimentoById.descricao,
      });

      formEditEmp.setFieldsValue({
        nome: empreendimentoById.nome,
        cidadeId: empreendimentoById.cidadeId,
        origem: empreendimentoById.origem,
        cronograma: empreendimentoById.cronograma,
        endereco: empreendimentoById.endereco,
        contato: empreendimentoById.contato,
        descricao: empreendimentoById.descricao,
      });

      setCronograma(empreendimentoById.cronograma);
    }
  }, [empreendimentoById, formEditEmp, isModalOpen]);

  // Atualiza estado local ao alterar o form
  const empreendimentoChange = (changedValues: any, allValues: any) => {
    changing.current = true;
    if (changing.current) {
      setEmpreendimento(allValues);
    }
  };

  // Mensagens de sucesso/erro
  const [errorDisplayed, setErrorDisplayed] = useState(false);
  const [successDisplayed, setSuccessDisplayed] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      if (success && !successDisplayed) {
        showSuccess(success);
        setSuccessDisplayed(true);
        setTimeout(() => {
          navigate(0); // ou handleFechar() dependendo da lógica
        }, 2000);
      }

      if (error && !errorDisplayed) {
        showError(error);
        setErrorDisplayed(true);
        setTimeout(() => {
          resetError();
          setErrorDisplayed(false);
        }, 3000);
      }
    }

    if (!isModalOpen) {
      setSuccessDisplayed(false);
      setErrorDisplayed(false);
    }
  }, [
    isModalOpen,
    success,
    error,
    showSuccess,
    showError,
    resetError,
    successDisplayed,
    errorDisplayed,
    navigate,
  ]);

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      okText="Atualizar"
      confirmLoading={isLoadingAtualizar}
      onOk={() =>
        atualizarEmpreendimento(empreendimentoById?.id, empreendimento)
      }
      okButtonProps={{
        style: {
          backgroundColor: "#003644",
          borderColor: "#003644",
          color: "white",
        }, // Botão "Ativar" com fundo verde
      }}
      cancelButtonProps={{
        style: {
          backgroundColor: "#f44336",
          borderColor: "#f44336",
          color: "white",
        }, // Botão "Cancelar" com fundo vermelho
      }}
    >
      {isLoading && <Skeleton active />}
      {!isLoading && empreendimentoById && (
        <Card
          title={<Title>Empreendimento</Title>}
          style={{
            width: "100%",
            marginTop: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          {contextHolder}
          <Form
            {...layout}
            form={formEditEmp}
            onValuesChange={empreendimentoChange}
            name="empreendimento"
            //style={formStyle}
          >
            <Form.Item name="nome" label="Nome:" className="formItems">
              <Input />
            </Form.Item>

            <Form.Item name="cidadeId" label="Cidade" className="formItems">
              <Select
                showSearch
                className="selectCidades"
                placeholder="Selecione a cidade"
                optionFilterProp="children"
                filterOption={filterOption}
                filterSort={filterSort}
              >
                {cidades.map((cidade: Cidade) => (
                  <Select.Option
                    key={cidade.id}
                    value={cidade.id}
                    label={cidade.nome}
                  >
                    {cidade.nome}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="origem" label="Origem" className="formItems">
              <InputNumber min={0} />
            </Form.Item>

            <Form.Item
              name="cronograma"
              label="Cronograma"
              className="formItems"
            >
              <Radio.Group onChange={cronogramaChange} value={cronograma}>
                <Radio value={1}>Sim</Radio>
                <Radio value={0}>Não</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="contato" label="Contato" className="formItems">
              <Input />
            </Form.Item>

            <Form.Item name="endereco" label="Endereço" className="formItems">
              <Input />
            </Form.Item>

            <Form.Item name="descricao" label="Descrição" className="formItems">
              <Input.TextArea />
            </Form.Item>
          </Form>
        </Card>
      )}
    </Modal>
  );
}
